"use client";

import { useRef } from "react";

import { PUBLIC_LIST_HERO_HEIGHT } from "@/components/host-panel/catalog/catalog-hero-metrics";
import { CatalogSection } from "@/components/host-panel/catalog/catalog-section";
import { LenisScrollProvider } from "@/components/pdp/lenis-scroll-provider";
import { PublicSiteHeader } from "@/components/shared/public-site-header";
import { PANEL_REVEAL, useReveals } from "@/lib/motion/use-reveals";
import { suggestionPieceHref } from "@/lib/pdp/pdp-canonical";
import { adoptSuggestionHref } from "@/lib/pdp/suggestion-surface";
import type { SuggestionList } from "@/lib/suggestion-lists/suggestion-lists";

/** The list as the catalog's suggestion hero reads it, and where "Quero essa lista" leads. */
export function catalogSuggestion(
  list: Pick<SuggestionList, "name" | "titleLead" | "titleAccent" | "description" | "countLabel"> & {
    coverUrl: string | null;
    /** The template's id on the backend (UUID): what the adoption event names it by (R-9). */
    id?: string;
    slug?: string;
    pieces?: readonly unknown[];
  },
  adoptHref: string,
) {
  return {
    name: list.name,
    titleLead: list.titleLead,
    titleAccent: list.titleAccent,
    description: list.description,
    coverUrl: list.coverUrl,
    countLabel: list.countLabel,
    adoptHref,
    // What the adoption event reports: the template's `id` (never a name-bearing slug, INST §03) and how
    // many pieces it brings. The slug stands in only when the id is missing, as a fixture's may be.
    ...(list.slug === undefined || list.pieces === undefined
      ? {}
      : { analytics: { suggestionId: list.id ?? list.slug, itemsCount: list.pieces.length } }),
  };
}

/**
 * One of the house's suggestion lists, as a page of its own: the design's catalog under the
 * `sugestao` context (`Catalogo.dc.html?contexto=Lista de sugestão&lista=…`).
 *
 * The same `CatalogSection` the host manages and the guest reads, under `audience="suggestion"`,
 * so the three views of a list never drift apart. The frame is the guest's page
 * (`public-list-view.tsx`), with the same reasons for `tb-panel-root` and `overflow-x-clip`.
 */
/**
 * What the page hands over. `coverUrl` is nullable because the backend's template may carry no
 * `featuredImageUrl`; the hero already draws without one.
 */
export type SuggestionListViewModel = Pick<
  SuggestionList,
  "slug" | "name" | "titleLead" | "titleAccent" | "description" | "countLabel" | "pieces"
> & { coverUrl: string | null; id?: string };

export function SuggestionListView({ list }: { list: SuggestionListViewModel }) {
  const rootRef = useRef<HTMLDivElement>(null);
  useReveals(rootRef, PANEL_REVEAL);

  return (
    <div ref={rootRef} className="tb-panel-root tb-editorial-body flex min-h-svh flex-col overflow-x-clip bg-tb-white">
      <LenisScrollProvider />

      <PublicSiteHeader homeHref="/" sectionHrefPrefix="/" surface="solid" activeSection="#listas" />

      <main className="flex-1 pt-[75px]">
        <div className="mx-auto w-full max-w-[1280px] px-[clamp(20px,4vw,48px)] animate-[tb-fade-in_280ms_var(--tb-ease-linen)_both]">
          <CatalogSection
            audience="suggestion"
            // Under the fixed 75px header the hero is the rest of the viewport, as on the guest's
            // page: the reference's suggestion hero measures 825px at 1440×900.
            heroHeight={PUBLIC_LIST_HERO_HEIGHT}
            pieces={list.pieces}
            canWrite={false}
            /*
             * Every piece opens its own page (the person's ruling of 2026-10-15).
             *
             * This surface used to hand out no address at all, on the reading that a suggestion
             * is a curation to read through and the one action it offers is taking the whole
             * list. The note left here then — that the pieces had become the backend's, so a PDP
             * was now possible — is what this change collects: someone browsing a house list
             * wants to look at a piece before wanting the list.
             *
             * The catalog's own page, still with no `contexto` (there is no list to stay inside of,
             * and claiming the host's would only be refused by the door on the other side) — but
             * now carrying the list it was opened from, so the page can say so and adopt the
             * whole list (`suggestionPieceHref`).
             *
             * `piece.id` is the `Product.id` — these pieces come through
             * `fromBackendGiftListItem`, which keys them by the product — so this is the same
             * address the panel's grid hands out for a piece that is on no list.
             */
            // Declares the public surface: the PDP's adopt/back links stay on the public site.
            pieceHref={(piece) => suggestionPieceHref(piece.id, list.slug, "publica")}
            suggestion={catalogSuggestion(list, adoptSuggestionHref(list.slug))}
          />
        </div>
      </main>
      {/*
        No footer, for the same reason as the guest's page: `Catalogo.dc.html` — which this
        surface runs under `hdrSug` — has no `<footer>` and no copyright line. The bar that stood
        here is the panel's (`host-footer.tsx`, handoff §4) and belongs only there.
      */}
    </div>
  );
}
